import type { FC } from 'react';

import { ShoppingBag } from '@/components/ShoppingBag';
import type { CheckoutFragment, MoneyFragment } from '@/graphql/saleor/api';
import { VOUCHER_TYPE } from '@/lib/const';
import { checkIfVatIncluded } from '@/lib/regions';

import type { OrderDataFragment } from './TransactionSummary.utils';
import {
  calculateSubtotal,
  formatCheckoutOrder,
  getReverseDiscount,
} from './TransactionSummary.utils';

import * as S from './SubTotalMobileSummary.styles';

export interface SubTotalMobileSummaryProps {
  channel?: string;
  coSalesTax?: MoneyFragment;
  fragment?: Partial<CheckoutFragment> | OrderDataFragment | null;
  isColoradoBilling?: boolean;
  salesTax?: MoneyFragment;
  showTax: boolean;
  voucherType?: string;
}

export const SubTotalMobileSummary: FC<SubTotalMobileSummaryProps> = ({
  channel,
  coSalesTax,
  fragment,
  isColoradoBilling = false,
  salesTax,
  showTax = true,
  voucherType,
}) => {
  if (!fragment) return null;
  const { discount, shippingPrice, subtotal, subtotalGross } =
    formatCheckoutOrder(fragment);
  const subtotalWithDiscount = calculateSubtotal(
    checkIfVatIncluded(channel) ? subtotalGross : subtotal,
    discount,
    voucherType,
  );

  return (
    <S.SubTotalSummaryWrapper data-testid='SubTotalSummaryWrapper'>
      <ShoppingBag.SubtotalPrice
        data-testid='ShoppingBagSubtotalPrice'
        price={subtotalWithDiscount}
      />

      {voucherType === VOUCHER_TYPE.FIXED &&
        discount &&
        discount.amount > 0 && (
          <ShoppingBag.Discount
            data-testid='ShoppingBagDiscount'
            price={getReverseDiscount(discount)}
          />
        )}

      <ShoppingBag.ShippingPrice
        data-testid='ShoppingBagShippingPrice'
        price={shippingPrice}
      />

      {showTax && (
        <ShoppingBag.SalesTax
          channel={channel}
          data-testid='ShoppingBagSalesTax'
          displayZero={true}
          price={salesTax}
        />
      )}
      {isColoradoBilling && coSalesTax?.amount !== 0 && (
        <ShoppingBag.RetailDeliveryFeesTax
          data-testid='ShoppingBagRetailDeliveryFeesTax'
          price={coSalesTax}
        />
      )}
    </S.SubTotalSummaryWrapper>
  );
};
